import React, { lazy } from 'react';
import { ArrowRight, PhoneIcon } from 'lucide-react';
type MiniLink = {
  label: string;
  href: string;
};
type Card = {
  title: string;
  subtitle: string;
  cta: string;
  href: string;
  images?: string[];
  image?: string;
  alt?: string;
  helperCue?: string;
  miniLinks?: MiniLink[];
  isSupport?: boolean;
  phone?: string;
  imageFit?: 'cover' | 'contain';
};
const CARDS: Card[] = [{
  title: 'Mailboxes',
  subtitle: 'Curbside, wall mount, locking, column, multi mount, and complete mailbox and post systems.',
  cta: 'Shop mailboxes',
  href: '/6m8/mailboxes.html',
  image: 'https://asset.imagine.art/processed/144bf9c3-fd5d-4976-884b-2ff61451a315',
  alt: 'Close-up of a residential curbside mailbox with a softly blurred house in the background.',
  imageFit: 'cover',
  miniLinks: [{
    label: 'Mailbox Posts',
    href: '/6m8/mailbox-posts.html'
  }, {
    label: 'Multi Mount Systems',
    href: '/6m8/mailboxes/multi-mount-mailbox-systems.html'
  }, {
    label: 'Parts & Accessories',
    href: '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html'
  }]
}, {
  title: 'Mailbox Posts',
  subtitle: 'Single, double, decorative, and replacement mailbox posts, grouped under Mailboxes.',
  cta: 'Shop mailbox posts',
  href: '/6m8/mailbox-posts.html',
  image: 'https://asset.imagine.art/processed/e5f6dc67-5f56-4bf3-8727-b2ed09d80243',
  alt: 'New residential mailbox post set in clean concrete near a curb, with mailbox ready to mount.',
  imageFit: 'cover'
}, {
  title: 'Commercial Mailboxes & Parcel Lockers',
  subtitle: 'Cluster mailboxes, CBUs, 4C systems, parcel lockers, collection boxes, and commercial parts.',
  cta: 'Shop commercial',
  href: '/6m8/commercial-mailboxes-lockers.html',
  image: 'https://asset.imagine.art/processed/ef04a6c9-560d-4d10-abf6-413035546153',
  alt: 'Commercial mailbox product collage with CBUs, vertical mailboxes, parcel lockers, and 4C systems.',
  imageFit: 'cover'
}, {
  title: 'Street Signs & Traffic Signs',
  subtitle: 'Street signs, stop signs, yield signs, speed limit signs, parking signs, and traffic signage.',
  cta: 'Shop street signs',
  href: '/6m8/street-signs-traffic-signs.html',
  image: 'https://asset.imagine.art/processed/815cbacc-4d38-4da6-b08d-60c94faec526',
  alt: 'Stop, yield, and no parking signs installed on a residential street.',
  imageFit: 'cover'
}, {
  title: 'Decorative Signage',
  subtitle: 'Decorative signage, sign frames, bases and hardware, and message boards.',
  cta: 'Shop decorative signage',
  href: '/6m8/decorative-signage.html',
  image: 'https://asset.imagine.art/processed/aca3d038-724e-4cdc-8e50-14a7642db219',
  alt: 'Ornamental decorative sign post with scroll bracket, brass sign plates, finial, and base in a landscaped entrance.',
  imageFit: 'cover'
}, {
  title: 'Outdoor Accessories & Home Accents',
  subtitle: 'Address plaques and house numbers, planters and window boxes, garden decor, country bells, patio coolers, and door chimes.',
  cta: 'Shop outdoor accessories',
  href: '/6m8/outdoor-accessories-home-accents.html',
  image: 'https://asset.imagine.art/processed/1e4d4fe0-127c-4b1b-aefe-c6cdc41166ef',
  alt: 'Cast-aluminum country bell with flower planters and window box planters in a garden porch setting.',
  imageFit: 'cover'
}, {
  title: 'Outdoor Lighting',
  subtitle: 'Outdoor lamp posts, post lights and fixtures, commercial lighting, and light blocks.',
  cta: 'Shop outdoor lighting',
  href: '/6m8/lighting.html',
  image: 'https://asset.imagine.art/processed/c59c5162-3c51-4223-ae7b-43df6c6e01a6',
  alt: 'Matching outdoor lamp posts with glowing lantern fixtures along a residential walkway.',
  imageFit: 'cover'
}, {
  title: 'Need Help Choosing?',
  subtitle: 'Not sure which mailbox, post, CBU, sign, or lighting product fits your project? The MidAtlantic team can help narrow the options.',
  cta: 'Get help choosing',
  href: '/ContactUs.html',
  isSupport: true,
  phone: '301-620-7585'
}];
function CardImage({
  card
}: {
  card: Card;
}) {
  if (card.images && card.images.length > 0) {
    return <a href={card.href} aria-label={card.title} className="product-image-stage h-44 grid grid-cols-2 grid-rows-2 gap-px bg-brand-border/40 border-b border-brand-border/50 overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-maroon" data-id="element-248" data-mp="w02zipjs4513">
        {card.images.slice(0, 4).map((src, i) => <div key={i} className="bg-white flex items-center justify-center p-2 overflow-hidden" data-id="element-249" data-mp="d80mrpa9d8w4" data-mpi={i}>
            <img src={src} alt="" aria-hidden="true" loading="lazy" className="max-h-full max-w-full object-contain mix-blend-multiply transform group-hover:scale-105 transition-transform duration-500 ease-out" data-id="element-250" data-mp="1jnayyquj0jv7" />
          </div>)}
      </a>;
  }
  const fitClass = card.imageFit === 'cover' ? 'object-cover' : 'object-contain';
  return <a href={card.href} className="product-image-stage aspect-[15/7] w-full flex items-center justify-center border-b border-brand-border/50 overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-maroon" data-id="element-251" data-mp="2zcsfq280zdnz">
      <img src={card.image} alt={card.alt || card.title} className={`h-full w-full ${fitClass} object-center transform group-hover:scale-105 transition-transform duration-500 ease-out`} data-id="element-252" data-mp="2lf45dobl3ox6" />
    </a>;
}
function SupportCard({
  card
}: {
  card: Card;
}) {
  return <div className="group flex flex-col rounded-xl border border-brand-maroon/30 bg-brand-goldWash shadow-sm hover:shadow-md hover:border-brand-maroon/60 hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-id="element-253" data-mp="epfsyv6snim2">
      <div className="aspect-[15/7] w-full flex items-center justify-center bg-brand-maroon/5 border-b border-brand-maroon/20" data-id="element-254" data-mp="24506mhujcaig">
        <div aria-hidden="true" className="relative h-16 w-24" data-id="element-255" data-mp="28y2tk74t4lpo">
          <div className="absolute left-1 top-3 h-10 w-14 rounded-xl border-2 border-brand-maroon bg-brand-goldWash" data-id="element-256" data-mp="1u8wwoptfz7ky">
            <span className="absolute inset-0 flex items-center justify-center text-[25px] font-bold leading-none text-brand-maroon" data-id="element-257" data-mp="1z7hwio4s7p8v">
              ?
            </span>
            <span className="absolute -bottom-1 left-3 h-3 w-3 rotate-45 border-b-2 border-r-2 border-brand-maroon bg-brand-goldWash" data-id="element-258" data-mp="2rvvvp77lwrm0" />
          </div>
          <div className="absolute right-1 top-6 h-10 w-14 rounded-xl border-2 border-brand-maroon/70 bg-brand-goldWash/80" data-id="element-259" data-mp="2dxxi44140z7k">
            <span className="absolute -bottom-1 right-3 h-3 w-3 rotate-45 border-b-2 border-r-2 border-brand-maroon/70 bg-brand-goldWash/80" data-id="element-260" data-mp="12gxsjg65cyh8" />
          </div>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-grow" data-id="element-261" data-mp="16fya6vcw1v2n">
        <a href={card.href} className="focus:outline-none focus:underline" data-id="element-262" data-mp="yxqj9rnq9l5c">
          <h3 className="text-xl font-serif font-bold text-brand-charcoal mb-2 group-hover:text-brand-maroon transition-colors" data-id="element-263" data-mp="u06ye8wiya4f">
            {card.title}
          </h3>
        </a>

        <p className="text-brand-muted text-sm mb-4 flex-grow leading-relaxed" data-id="element-264" data-mp="13xv2hpg0stz9">
          {card.subtitle}
        </p>

        {card.phone && <a href={`tel:${card.phone}`} className="inline-flex items-center gap-2 text-brand-maroon font-bold text-base mb-4 hover:text-brand-maroonDark transition-colors focus:outline-none focus:underline" data-id="element-265" data-mp="g9265rvhkk44">
            <PhoneIcon size={16} data-id="element-266" data-mp="l7dfm1hvcslt" />
            {card.phone}
          </a>}

        <a href={card.href} className="inline-flex items-center justify-center bg-brand-maroon text-white font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-brand-maroonDark transition-colors mt-auto focus:outline-none focus:ring-2 focus:ring-brand-gold" data-id="element-267" data-mp="l5ah0t2nhr5h">
          {card.cta}
          <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-268" data-mp="3373uynuywv87" />
        </a>
      </div>
    </div>;
}
export function DecisionCards() {
  return <section id="shop-by-need" className="py-12 lg:py-16 bg-brand-background relative z-20 -mt-4 lg:-mt-8" data-id="element-269" data-mp="3h51u4m0pwl34">
      <div className="max-w-7xl mx-auto px-4" data-id="element-270" data-mp="3m2lgyabhe66o">
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-271" data-mp="3m1sj5yui2r0o">
          <span className="text-brand-maroon font-bold tracking-wider uppercase text-xs mb-2 block flex items-center justify-center gap-2" data-id="element-272" data-mp="3r1576mx3y44u">
            <span className="w-8 h-px bg-brand-gold" data-id="element-273" data-mp="4n5bi95lxhdg"></span>
            Shop by need
            <span className="w-8 h-px bg-brand-gold" data-id="element-274" data-mp="4p832xmerika"></span>
          </span>
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-275" data-mp="8qbgldgzh2ic">
            Start with the category you need
          </h2>
          <p className="text-lg text-brand-muted" data-id="element-276" data-mp="1y6dojr7ttyk0">
            Choose the catalog path that matches what you're shopping for.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-277" data-mp="1t5g93me7bim7">
          {CARDS.map(card => card.isSupport ? <SupportCard key={card.title} card={card} data-id="element-278" data-mp="1k6uqjr8qc3lf" /> : <div key={card.title} className="group flex flex-col bg-white rounded-xl border border-brand-border shadow-sm hover:shadow-md hover:border-brand-maroon/60 hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-id="element-279" data-mp="2gw3th75b00pn">
                <CardImage card={card} data-id="element-280" data-mp="234yqbv2q8qak" />

                <div className="p-6 flex flex-col flex-grow" data-id="element-281" data-mp="233w1rtsvmzxt">
                  <a href={card.href} className="focus:outline-none focus:underline" data-id="element-282" data-mp="30kcm1iare8po">
                    <h3 className="text-xl font-serif font-bold text-brand-charcoal mb-2 group-hover:text-brand-maroon transition-colors" data-id="element-283" data-mp="2vsuluwgk8c5r">
                      {card.title}
                    </h3>
                  </a>

                  <p className="text-brand-muted text-sm mb-4 flex-grow leading-relaxed" data-id="element-284" data-mp="3c1dqaee3k2d5">
                    {card.subtitle}
                  </p>

                  {card.helperCue && <p className="text-brand-charcoal text-xs font-medium bg-brand-goldSoft p-3 rounded-md mb-4 border border-brand-gold/30" data-id="element-285" data-mp="3lw5qzr2z75c5">
                      {card.helperCue}
                    </p>}

                  {card.miniLinks && <div className="flex flex-wrap gap-2 mb-4" data-id="element-286" data-mp="di6veqjx1lz6">
                      {card.miniLinks.map(link => <a key={link.label} href={link.href} className="text-xs bg-white hover:bg-brand-goldWash border border-brand-border text-brand-charcoal px-2.5 py-1 rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand-maroon" data-id="element-287" data-mp="8k5as16udell">
                          {link.label}
                        </a>)}
                    </div>}

                  <a href={card.href} className="inline-flex items-center text-brand-charcoal font-semibold text-sm hover:text-brand-maroon transition-colors mt-auto pt-4 border-t border-brand-border/30 focus:outline-none focus:underline" data-id="element-288" data-mp="s3ufycu16sik">
                    {card.cta}
                    <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform text-brand-maroon" data-id="element-289" data-mp="n7m2d3vhf7di" />
                  </a>
                </div>
              </div>)}
        </div>
      </div>
    </section>;
}